/**
 * AdminSecurityTab - Security Flags & Abuse Activity dashboard
 */
import { useState, useEffect } from "react";
import { adminAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Loader2, Shield, ShieldAlert, ShieldCheck, RefreshCw,
  Users, Smartphone, Lock, Unlock, Eye, ChevronLeft, ChevronRight,
  AlertTriangle, Clock, X
} from "lucide-react";

function SummaryCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">{label}</p>
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
      </div>
      <p className="text-xl font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
        {value}
      </p>
    </div>
  );
}

function FlagReasonBadge({ reason }) {
  const configs = {
    "BURST_DOWNLOAD_DETECTED": { label: "Burst Download", color: "bg-red-100 text-red-700" },
    "SEQUENTIAL_SCRAPING_DETECTED": { label: "Scraping", color: "bg-orange-100 text-orange-700" },
    "PREVIEW_SKIP_DETECTED": { label: "Preview Skip", color: "bg-amber-100 text-amber-700" },
    "MAX_DEVICES_EXCEEDED": { label: "Max Devices", color: "bg-purple-100 text-purple-700" },
  };
  const config = configs[reason] || { label: reason, color: "bg-slate-100 text-slate-600" };
  return (
    <Badge className={`${config.color} text-[10px]`}>{config.label}</Badge>
  );
}

function FlagDetailsModal({ flag, onClose, onResolve }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const res = await adminAPI.flagDetails(flag.user_id);
        setDetails(res.data);
      } catch (err) {
        toast.error("Failed to load details");
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [flag.user_id]);

  const handleResolve = async () => {
    setResolving(true);
    try {
      await adminAPI.resolveFlag(flag.user_id);
      toast.success("Flag resolved and lock removed");
      onResolve();
      onClose();
    } catch (err) {
      toast.error("Failed to resolve flag");
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center" data-testid="flag-details-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 p-6 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4 shrink-0">
          <div>
            <h3 className="text-lg font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
              Security Flag Details
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">
              User: {flag.phone || flag.user_id}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : details ? (
          <div className="flex-1 overflow-y-auto space-y-6">
            {/* Flag Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-[10px] text-slate-500 uppercase">Reason</p>
                <FlagReasonBadge reason={flag.flag_reason} />
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-[10px] text-slate-500 uppercase">Plan</p>
                <p className="text-sm font-medium">{flag.plan || "free"}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-[10px] text-slate-500 uppercase">Total Downloads</p>
                <p className="text-sm font-medium">{flag.total_downloads}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-[10px] text-slate-500 uppercase">Devices</p>
                <p className="text-sm font-medium">{flag.device_fingerprint_count}</p>
              </div>
            </div>

            {/* User Info */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="text-xs font-semibold text-slate-600 mb-3">User Information</h4>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-slate-500">Phone:</span>{" "}
                  <span className="font-medium">{flag.phone || "—"}</span>
                </div>
                <div>
                  <span className="text-slate-500">Email:</span>{" "}
                  <span className="font-medium">{flag.email || "—"}</span>
                </div>
                <div>
                  <span className="text-slate-500">Signup:</span>{" "}
                  <span className="font-medium">
                    {flag.signup_date ? new Date(flag.signup_date).toLocaleDateString("en-IN") : "—"}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Last Login:</span>{" "}
                  <span className="font-medium">
                    {flag.last_login ? new Date(flag.last_login).toLocaleDateString("en-IN") : "—"}
                  </span>
                </div>
              </div>
            </div>

            {/* Lock Info */}
            {details.active_lock && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="h-4 w-4 text-red-600" />
                  <h4 className="text-xs font-semibold text-red-700">Active Lock</h4>
                </div>
                <p className="text-xs text-red-600">
                  Expires: {new Date(details.active_lock.expires_at).toLocaleString("en-IN")}
                </p>
              </div>
            )}

            {/* Recent IPs */}
            {flag.recent_ip_addresses?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-600 mb-2">Recent IP Addresses</h4>
                <div className="flex flex-wrap gap-2">
                  {flag.recent_ip_addresses.map((ip, i) => (
                    <Badge key={i} variant="outline" className="font-mono text-[10px]">{ip}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Devices */}
            {details.devices?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-600 mb-2">Registered Devices ({details.devices.length})</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-3 py-2 text-[10px] font-medium text-slate-500">Fingerprint</th>
                        <th className="text-left px-3 py-2 text-[10px] font-medium text-slate-500">First Seen</th>
                        <th className="text-left px-3 py-2 text-[10px] font-medium text-slate-500">Last Seen</th>
                        <th className="text-right px-3 py-2 text-[10px] font-medium text-slate-500">Downloads</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {details.devices.map((device, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2 font-mono">{device.fingerprint_hash?.slice(0, 12)}...</td>
                          <td className="px-3 py-2">{new Date(device.first_seen).toLocaleDateString("en-IN")}</td>
                          <td className="px-3 py-2">{new Date(device.last_seen).toLocaleDateString("en-IN")}</td>
                          <td className="px-3 py-2 text-right font-medium">{device.download_count || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Recent Downloads Timeline */}
            {details.recent_downloads?.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-slate-600 mb-2">Recent Download Activity (Last 100)</h4>
                <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 text-[10px] font-medium text-slate-500">Time</th>
                        <th className="text-left px-3 py-2 text-[10px] font-medium text-slate-500">Image ID</th>
                        <th className="text-center px-3 py-2 text-[10px] font-medium text-slate-500">Preview</th>
                        <th className="text-center px-3 py-2 text-[10px] font-medium text-slate-500">Redownload</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {details.recent_downloads.slice(0, 50).map((dl, i) => (
                        <tr key={i}>
                          <td className="px-3 py-2">{new Date(dl.timestamp).toLocaleString("en-IN")}</td>
                          <td className="px-3 py-2 font-mono truncate max-w-[150px]" title={dl.public_id}>{dl.public_id}</td>
                          <td className="px-3 py-2 text-center">
                            {dl.preview_opened ? "✓" : "—"}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {dl.is_redownload ? "Yes" : "No"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : null}

        {/* Actions */}
        <div className="mt-4 pt-4 border-t flex justify-end gap-3 shrink-0">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {!flag.resolved && (
            <Button 
              onClick={handleResolve} 
              disabled={resolving}
              className="bg-green-600 hover:bg-green-700"
            >
              {resolving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Unlock className="h-4 w-4 mr-2" />}
              Resolve & Unlock
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function AdminSecurityTab() {
  const [summary, setSummary] = useState(null);
  const [flags, setFlags] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flagsLoading, setFlagsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [showResolved, setShowResolved] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState(null);

  const fetchSummary = async () => {
    try {
      const res = await adminAPI.securitySummary();
      setSummary(res.data);
    } catch (err) {
      toast.error("Failed to load security summary");
    }
  };

  const fetchFlags = async (p = 1) => {
    setFlagsLoading(true);
    try {
      const res = await adminAPI.securityFlags({ page: p, limit: 20, resolved: showResolved ? null : false });
      setFlags(res.data);
      setPage(p);
    } catch (err) {
      toast.error("Failed to load security flags");
    } finally {
      setFlagsLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchSummary(), fetchFlags(1)]);
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    fetchFlags(1);
  }, [showResolved]);

  const handleRefresh = () => {
    fetchSummary();
    fetchFlags(page);
    toast.success("Security data refreshed");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="security-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-security-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#09090B]" style={{ fontFamily: "Manrope, sans-serif" }}>
            Security & Abuse Prevention
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Monitor flagged accounts and active security locks
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} className="text-xs">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard
            label="Active Locks"
            value={summary.active_locks}
            icon={Lock}
            color={summary.active_locks > 0 ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"}
          />
          <SummaryCard
            label="Unresolved Flags"
            value={summary.unresolved_flags}
            icon={ShieldAlert}
            color={summary.unresolved_flags > 0 ? "bg-orange-100 text-orange-600" : "bg-green-100 text-green-600"}
          />
          <SummaryCard
            label="Total Flags"
            value={summary.total_flags}
            icon={Shield}
            color="bg-slate-100 text-slate-600"
          />
          <SummaryCard
            label="Devices Tracked"
            value={summary.total_devices_tracked}
            icon={Smartphone}
            color="bg-blue-100 text-blue-600"
          />
        </div>
      )}

      {/* Flags by Reason */}
      {summary?.flags_by_reason && Object.keys(summary.flags_by_reason).length > 0 && (
        <div className="bg-slate-50 rounded-lg p-4">
          <h4 className="text-xs font-semibold text-slate-600 mb-3">Flags by Reason</h4>
          <div className="flex flex-wrap gap-3">
            {Object.entries(summary.flags_by_reason).map(([reason, count]) => (
              <div key={reason} className="flex items-center gap-2">
                <FlagReasonBadge reason={reason} />
                <span className="text-xs font-medium text-slate-600">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Flags List */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#09090B]">Security Flags</h3>
          <div className="flex items-center gap-2">
            <Button
              variant={showResolved ? "default" : "outline"}
              size="sm"
              onClick={() => setShowResolved(!showResolved)}
              className="text-xs"
            >
              {showResolved ? "Show All" : "Show Resolved"}
            </Button>
          </div>
        </div>

        {flagsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        ) : flags?.flags?.length > 0 ? (
          <>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">User</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Reason</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-slate-500">Plan</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-slate-500">Downloads</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-slate-500">Devices</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Flagged</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-slate-500">Status</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {flags.flags.map((flag, i) => (
                    <tr key={i} className="hover:bg-slate-50/50">
                      <td className="px-4 py-3">
                        <p className="text-xs font-medium">{flag.phone || "Unknown"}</p>
                        {flag.email && <p className="text-[10px] text-slate-400">{flag.email}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <FlagReasonBadge reason={flag.flag_reason} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant="outline" className="text-[10px]">
                          {flag.plan || "free"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center font-medium">{flag.total_downloads}</td>
                      <td className="px-4 py-3 text-center">{flag.device_fingerprint_count}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {new Date(flag.flag_timestamp).toLocaleDateString("en-IN")}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {flag.resolved ? (
                          <Badge className="bg-green-100 text-green-700 text-[10px]">Resolved</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 text-[10px]">Active</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedFlag(flag)}
                          className="text-xs"
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {flags.total_pages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-xs text-slate-500">
                  Page {flags.page} of {flags.total_pages} ({flags.total} total)
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => fetchFlags(page - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= flags.total_pages}
                    onClick={() => fetchFlags(page + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12 border rounded-lg bg-slate-50/50">
            <ShieldCheck className="h-12 w-12 text-green-400 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No security flags</p>
            <p className="text-xs text-slate-400 mt-1">All accounts are operating normally</p>
          </div>
        )}
      </div>

      {/* Flag Details Modal */}
      {selectedFlag && (
        <FlagDetailsModal
          flag={selectedFlag}
          onClose={() => setSelectedFlag(null)}
          onResolve={() => {
            fetchSummary();
            fetchFlags(page);
          }}
        />
      )}
    </div>
  );
}
